// Раскраска точек по невязке «модель − измерение».
//
// РАДИ ЭТОГО РАЗДЕЛ И ЗАТЕВАЛСЯ. Кабинет умеет показывать поле и умеет
// показывать измерение; разница между ними — единственная величина, по которой
// видно, где модель промахивается.
//
// ЦВЕТ НЕ МОЖЕТ ЗНАЧИТЬ ДВА. В обычном виде он несёт свежесть, поэтому невязка
// не подмешивается к силуэту, а рисуется ОРЕОЛОМ под ним: форма по-прежнему
// говорит про тип, силуэт — про возраст, ореол — про расхождение. Иначе
// пришлось бы выбирать, о чём умолчать.
//
// Шкала СМЫСЛОВАЯ и бренду не отдана — как прогноз, лёд и туман
// (`SEMANTIC_LAYERS` реестра палитр): здесь цвет это оценка, а не оформление.

/** Слои, которые сервер умеет сверить пакетом (см. `RESIDUAL_LAYERS` бэкенда). */
export const RESIDUAL_LAYERS = ['sst', 'salinity'] as const

export type ResidualLayer = (typeof RESIDUAL_LAYERS)[number]

export function isResidualLayer(value: string | null | undefined): value is ResidualLayer {
  return !!value && (RESIDUAL_LAYERS as readonly string[]).includes(value)
}

export interface ResidualStop {
  /** Граница; `color` — цвет ВЫШЕ неё. Ниже самой нижней лежит `belowColor`. */
  from: number
  color: string
}

export interface ResidualScale {
  unit: string
  /** Край шкалы: всё дальше него красится крайним цветом. */
  max: number
  /** Цвет ниже нижней границы. */
  belowColor: string
  stops: ResidualStop[]
}

// Расходящаяся шкала: холодная половина — модель ТЕПЛЕЕ измерения, тёплая —
// холоднее. Середина намеренно широкая: разница меньше четверти края у SST —
// это разница между «скином» поля и датчиком на метре, а не ошибка модели.
const NEG_STRONG = '#1d4ed8'
const NEG_MID = '#60a5fa'
const NEG_SOFT = '#bae6fd'
const NEUTRAL = '#e2e8f0'
const POS_SOFT = '#fed7aa'
const POS_MID = '#f97316'
const POS_STRONG = '#b91c1c'

function scaleOf(max: number, unit: string): ResidualScale {
  // Полосы отбиваются долями края: половина и четверть. Одна форма на оба
  // слоя — иначе шкалы разъехались бы по вкусу, а не по величине.
  const half = max / 2
  const quarter = max / 4
  return {
    unit,
    max,
    belowColor: NEG_STRONG,
    stops: [
      { from: -max, color: NEG_MID },
      { from: -half, color: NEG_SOFT },
      { from: -quarter, color: NEUTRAL },
      { from: quarter, color: POS_SOFT },
      { from: half, color: POS_MID },
      { from: max, color: POS_STRONG },
    ],
  }
}

// Края шкал РАЗНЫЕ и куплены величиной, а не вкусом: ±2 °C поверхности — это
// уже другая вода (фронт, апвеллинг), а для солёности столько же значат
// ±0,4 psu. Общая шкала расплющила бы солёность в нейтральный цвет и объявила
// бы модель безупречной там, где она ошибается.
const SCALES: Record<ResidualLayer, ResidualScale> = {
  sst: scaleOf(2, 'degrees_C'),
  salinity: scaleOf(0.4, 'psu'),
}

export function residualScale(layer: string): ResidualScale {
  return SCALES[layer as ResidualLayer] ?? SCALES.sst
}

/** Цвет невязки; `null` — красить нечем.
 *
 *  Пропуск НЕ приравнивается к нулю: точка без сопоставимого измерения обязана
 *  остаться без ореола, иначе молчание Argo читалось бы как похвала модели. */
export function residualColor(
  diff: number | null | undefined,
  layer: string,
): string | null {
  if (diff == null || !Number.isFinite(diff)) return null
  const scale = residualScale(layer)

  let color = scale.belowColor
  for (const stop of scale.stops) {
    if (diff >= stop.from) color = stop.color
  }
  return color
}

/** Полосы легенды сверху вниз: от «измерение теплее» к «измерение холоднее».
 *
 *  `from`/`to` — границы полосы; `null` значит «без края», и подпись у такой
 *  полосы обязана читаться как «и дальше», а не как число. */
export function legendBands(
  layer: string,
): Array<{ color: string; from: number | null; to: number | null }> {
  const scale = residualScale(layer)
  const bands = [
    { color: scale.belowColor, from: null as number | null, to: scale.stops[0].from as number | null },
    ...scale.stops.map((stop, i) => ({
      color: stop.color,
      from: stop.from as number | null,
      to: (scale.stops[i + 1]?.from ?? null) as number | null,
    })),
  ]
  return bands.reverse()
}

/** Ореол рисуется ТОЛЬКО там, где невязка есть. */
export function haloFilter(): unknown[] {
  return ['has', 'resid']
}

/** data-driven `circle-color` ступенями шкалы слоя. */
export function haloColorExpr(layer: string): unknown[] {
  const scale = residualScale(layer)
  const expr: unknown[] = ['step', ['get', 'resid'], scale.belowColor]
  for (const stop of scale.stops) expr.push(stop.from, stop.color)
  return expr
}
